'use client';

import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useMarketsSortedByCriteria from '../../hooks/useMarketsSortedByCriteria';
import { MarketId } from '@velocity-exchange/common';
import { Menu, Close, Star } from '@velocity-exchange/icons';
import { PriceChangePercentDisplay } from '../MarketStatsBar/PriceChangePercentDisplay';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { cj, cm } from 'src/utils/classNames';
import useMarketStateStore from 'src/stores/useMarketStateStore';
import useDevSwitchIsOn from '../../hooks/useDevSwitchIsOn';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { WithClassName } from 'src/utils/UtilityTypes';
import { Z_INDEX } from 'src/constants/zIndex';

const MarketBar = () => {
	const [hiddenMarketsStartIndex, setHiddenMarketsStartIndex] =
		useState(Infinity);
	const [showExpandButton, setShowExpandButton] = useState(true);
	const [expanded, setExpanded] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const innerBarRef = useRef<HTMLDivElement>(null);

	const currentlySelectedMarketId = useDriftStore(
		(s) => s.selectedMarket.current?.marketId
	);

	const devSwitchIsOn = useDevSwitchIsOn();

	const getMarketHasLiquidity = useMarketStateStore(
		(s) => s.getMarketHasLiquidity
	);

	const { switchMarket } = useSwitchMarket();

	const handleClickedCollapseButton = useCallback(() => {
		setExpanded(false);
	}, []);

	const handleSelection = useCallback(
		(marketId: MarketId) => {
			switchMarket({
				marketIndex: marketId.marketIndex,
				marketType: marketId.marketType,
			});
			handleClickedCollapseButton();
		},
		[switchMarket, handleClickedCollapseButton]
	);

	const markets = useMarketsSortedByCriteria({
		marketType: currentlySelectedMarketId?.marketType,
		sortBy: ['isFavorited'],
	});

	const favoriteMarkets = useMemo(() => {
		return markets.filter((market) => market.isFavourited);
	}, [markets]);

	const handleCollapse = useCallback((event: MouseEvent) => {
		// If event comes from inside of wrapperRef, don't collapse
		let node = event.target as HTMLElement;
		while (node) {
			if (node === wrapperRef.current) {
				return;
			}
			node = node.parentNode as HTMLElement;
		}

		setExpanded(false);
		document.body.removeEventListener('click', handleCollapse);
	}, []);

	const handleExpand = useCallback(() => {
		setExpanded(true);

		const timeoutId = setTimeout(() => {
			document.body.addEventListener('click', handleCollapse);
		}, 0);

		// Clean up timeout if component unmounts before it fires
		return () => {
			clearTimeout(timeoutId);
		};
	}, [handleCollapse]);

	const marketBarMarkets: MarketBarMarket[] = useMemo(() => {
		const marketBarInfo = favoriteMarkets
			.map((mktInfo) => ({
				marketIndex: mktInfo.marketIndex,
				marketId: new MarketId(mktInfo.marketIndex, mktInfo.marketType),
				baseAssetSymbol: mktInfo.baseAssetSymbol,
			}))
			.filter(
				(mktBarInfo) =>
					devSwitchIsOn || getMarketHasLiquidity(mktBarInfo.marketId)
			);

		return marketBarInfo;
	}, [favoriteMarkets, devSwitchIsOn, getMarketHasLiquidity]);

	const marketButtons = useMemo(
		() =>
			marketBarMarkets.map((marketInfo, index) => (
				<MarketButton
					key={`${marketInfo.baseAssetSymbol}_${index}`}
					className={cj(
						'w-full justify-center',
						index >= hiddenMarketsStartIndex && 'invisible'
					)}
					marketBarMarket={marketInfo}
					selected={currentlySelectedMarketId?.key === marketInfo.marketId.key}
					onSelect={handleSelection}
				/>
			)),
		[
			marketBarMarkets,
			currentlySelectedMarketId?.key,
			handleSelection,
			expanded,
			hiddenMarketsStartIndex,
		]
	);

	// when innerBarRef's children are wider than wrapperBarRef, show button to expand
	const handleWindowResize = useCallback(() => {
		if (!wrapperRef.current || !innerBarRef.current) {
			return;
		}

		const wrapperBoundingRect = wrapperRef.current.getBoundingClientRect();
		let childrenWidth = 0;
		let hiddenMarketsStartIndex = 0;

		if (innerBarRef.current?.children?.length) {
			[...innerBarRef.current.children].forEach((node) => {
				if (node) {
					childrenWidth += node.getBoundingClientRect().width;
					if (childrenWidth < wrapperBoundingRect.width) {
						hiddenMarketsStartIndex += 1;
					}
				}
			});
		}

		setHiddenMarketsStartIndex(hiddenMarketsStartIndex - 1);

		if (wrapperBoundingRect.width > childrenWidth && showExpandButton) {
			setShowExpandButton(false);
			setExpanded(false);

			document.body.removeEventListener('click', handleCollapse);
		}

		if (
			wrapperBoundingRect.width < childrenWidth &&
			!showExpandButton &&
			!expanded
		) {
			setShowExpandButton(true);
		}
	}, [showExpandButton, expanded, handleCollapse]);

	useEffect(() => {
		// Wait for next render cycle - ensures that marketButtons are rendered
		const timer = setTimeout(() => {
			handleWindowResize();
		}, 0);

		return () => clearTimeout(timer);
	}, [marketButtons, handleWindowResize]);

	useEffect(() => {
		window.addEventListener('resize', handleWindowResize);

		return () => {
			window.removeEventListener('resize', handleWindowResize);
		};
	}, [handleWindowResize]);

	// Cleanup click listener when component unmounts
	useEffect(() => {
		return () => {
			document.body.removeEventListener('click', handleCollapse);
		};
	}, [handleCollapse]);

	return (
		<div className="relative flex h-full items-center" ref={wrapperRef}>
			<div className="relative size-full overflow-hidden rounded-sm bg-container-bg px-md py-2xs xl:px-lg xl:py-xs">
				<div className="flex size-full items-center" ref={innerBarRef}>
					<Tooltip
						content="Favorite markets"
						className="flex text-star-yellow"
						decoration="cursor-help"
					>
						<Star size={16} />
					</Tooltip>
					{marketButtons}
				</div>
				{showExpandButton && (
					<div
						className={cj(
							'absolute top-0 right-0 flex h-full w-[40px] items-center justify-end transition-colors',
							'rounded-r-sm bg-linear-to-r from-transparent to-button-secondary-bg to-50%',
							'cursor-pointer pr-1 select-none hover:to-button-secondary-bg-hover',
							'to-container-bg text-text-secondary hover:text-text-default'
						)}
						onClick={expanded ? handleClickedCollapseButton : handleExpand}
					>
						{expanded ? (
							<Close size={18} className="rotate-90" />
						) : (
							<Menu size={18} className="rotate-90" />
						)}
					</div>
				)}
			</div>
			{expanded && (
				<div
					className={cj(
						'absolute top-full right-0 rounded-sm p-sm',
						'h-auto border border-input-border bg-container-bg shadow-lg'
					)}
					style={{ zIndex: Z_INDEX.popup }}
				>
					<div className="flex thin-scroll max-h-[600px] flex-col gap-y-sm overflow-auto">
						{marketBarMarkets
							.slice(hiddenMarketsStartIndex, marketBarMarkets.length)
							.map((marketInfo) => (
								<MarketButton
									key={marketInfo.baseAssetSymbol}
									marketBarMarket={marketInfo}
									selected={
										currentlySelectedMarketId?.key === marketInfo.marketId.key
									}
									onSelect={handleSelection}
								/>
							))}
					</div>
				</div>
			)}
		</div>
	);
};

type MarketBarMarket = {
	marketId: MarketId;
	marketIndex: number;
	baseAssetSymbol: string;
};

interface MarketButtonProps extends WithClassName {
	marketBarMarket: MarketBarMarket;
	selected: boolean;
	onSelect: (market: MarketId) => void;
}

const MarketButton = (props: MarketButtonProps) => {
	const buttonElement = useRef<HTMLDivElement>(null);

	return (
		<div className="relative" ref={buttonElement}>
			<div
				className={cm(
					'relative w-full bg-container-bg transition-colors select-none',
					'hover:bg-button-secondary-bg hover:text-text-default',
					'flex max-h-[28px] shrink-0 cursor-pointer items-center gap-x-xs',
					'rounded-sm p-sm',
					props.selected ? 'text-text-default' : 'text-text-secondary',
					props.className
				)}
				id={`marketButton${props.marketBarMarket.baseAssetSymbol}`}
				onClick={() => {
					props.onSelect(props.marketBarMarket.marketId);
				}}
			>
				<span className="typo-body-sm">
					{props.marketBarMarket.baseAssetSymbol}
				</span>
				<span className="min-w-[50px]">
					<PriceChange24hDisplay marketId={props.marketBarMarket.marketId} />
				</span>
			</div>
		</div>
	);
};

const PriceChange24hDisplay = ({ marketId }: { marketId: MarketId }) => {
	const markPrice = useMarkPrice(marketId);
	return (
		<PriceChangePercentDisplay
			className="typo-label-sm"
			marketId={marketId}
			markPrice={markPrice}
		/>
	);
};

export default React.memo(MarketBar);
